import { computed, reactive, ref } from "vue";
import { addNotification, notifications } from "./notifications";
import { locale } from "../locale";
import { isSelfAdmin } from "./adminsState";
import { ws_instance } from "../websocket/ws-instance";
import { fetcher } from "../api/fetcher";
import { SelectTab } from "./viewState";
import { activeRoomState } from "./active_room";

const notId = ref("");

const isShow = computed(() => {
  return !!notifications.find((n) => n.id === notId.value);
});

export function showWaitRoomNotification() {
  if(!activeRoomState.selfApiConnected || usersInWaitRoom.length === 0) return;
  if (isShow.value || !isSelfAdmin.value) return;
  const id = addNotification(
    0,
    locale.waitRoom.notification.text,
    undefined,
    true,
    {
      text: locale.waitRoom.notification.button,
      action: () => {
        SelectTab("users");
      },
    },
  );
  notId.value = id;
}

function removeWaitRoomNotification() {
  if (isShow.value && notId.value) {
    const not = notifications.find((n) => n.id === notId.value);
    not?.clear();
  }
}

export const usersInWaitRoom = reactive<RequestUser[]>([]);

export function subscribeWaitRoomEvents(rid?: string) {
  ws_instance.addEventListener("enter-request", (data: RequestUser) => {
    addUser(data);
    showWaitRoomNotification();
  });
  ws_instance.addEventListener("enter-request-remove", (data: string) => {
    removeById(data);
    if (usersInWaitRoom.length === 0) {
      removeWaitRoomNotification();
    }
  });
  ws_instance.addEventListener("enter-request-approved", (data: string) => {
    removeById(data);
    if (usersInWaitRoom.length === 0) {
      removeWaitRoomNotification();
    }
  });
  ws_instance.addEventListener("enter-request-rejected", (data: string) => {
    removeById(data);
    if (usersInWaitRoom.length === 0) {
      removeWaitRoomNotification();
    }
  });
  ws_instance.addEventListener(
    "enter-request-approved-list",
    (data: string[]) => {
      data.forEach((item) => {
        removeById(item);
      });
      if (usersInWaitRoom.length === 0) {
        removeWaitRoomNotification();
      }
    },
  );
  ws_instance.addEventListener(
    "enter-request-rejected-list",
    (data: string[]) => {
      data.forEach((item) => {
        removeById(item);
      });
      if (usersInWaitRoom.length === 0) {
        removeWaitRoomNotification();
      }
    },
  );
  if (rid)
    fetcher
      .get({
        path: `/wait-room/${rid}`,
      })
      .then((data: { data: RequestUser[] }) => {
        data.data.forEach((u) => addUser(u));
        if (data.data.length > 0) {
          showWaitRoomNotification();
        }
      })
      .catch(() => {});
}

export async function rejectUser(userId: string) {
  await fetcher.post({
    path: "/wait-room/reject",
    payload: {
      userId,
    },
  });
}

export async function approveUser(userId: string) {
  await fetcher.post({
    path: "/wait-room/approve",
    payload: {
      userId,
    },
  });
}

export async function rejectUserAll() {
  await fetcher.post({
    path: "/wait-room/rejectAll",
    payload: {},
  });
}

export async function approveUserAll() {
  await fetcher.post({
    path: "/wait-room/approveAll",
    payload: {},
  });
}

export async function revokeUserEnter(userId: string) {
  await fetcher.delete({
    path: "/wait-room/approve",
    payload: {userId},
  });
}

export function clearStateAndUnsubscribe() {
  ws_instance.removeAllEventListeners("enter-request");
  ws_instance.removeAllEventListeners("enter-request-remove");
  ws_instance.removeAllEventListeners("enter-request-approved");
  ws_instance.removeAllEventListeners("enter-request-rejected");
  ws_instance.removeAllEventListeners("enter-request-approved-list");
  ws_instance.removeAllEventListeners("enter-request-rejected-list");
  usersInWaitRoom.length = 0;
}

function removeById(id: string) {
  const idx = usersInWaitRoom.findIndex((u) => u.id === id);
  if (idx > -1) {
    usersInWaitRoom.splice(idx, 1);
  }
}

function addUser(data: RequestUser) {
  const exist = usersInWaitRoom.find((u) => u.id === data.id);
  if (!exist) {
    usersInWaitRoom.push(data);
  }
}

export function addUsersInWaitState(users: RequestUser[]) {
  users.forEach(u => addUser(u))
}

export interface RequestUser {
  id: string;
  displayedName: string;
  avatar: string;
}
